import './MobileNavigationOffcanvas.scss';

import { b2x } from '@b2x/react/src';
import classnames from 'classnames';
import React from 'react';

import { Button } from './Button';
import { Container } from './Container';
import { MenuMobileOffcanvasItemContentType } from './contentTypes';
import { t } from './i18n/i18n';
import { Icon, isIconName } from './Icon';
import srcMobile from './images/logo-mobile.svg';
import { SocialIcons } from './SocialIcons';

interface MobileNavigationOffcanvasProps extends b2x.OffcanvasProps {}

const MobileNavigationOffcanvas = (props: MobileNavigationOffcanvasProps) => {
  const menu = b2x.useMenu('MENU_HEADER_MOBILE', { populate: { content: true } });

  return (
    <b2x.Offcanvas className="mobile-navigation-offcanvas" {...props}>
      <div className="d-flex flex-column h-100 bg-black px-3">
        <Header />
        <div className="flex-grow-1 position-relative">
          <b2x.Drawers>
            <div className="d-flex flex-column h-100">
              <div className="flex-grow-1">{menu && <DrawerMenuItems item={menu} />}</div>
              <Footer />
            </div>
          </b2x.Drawers>
        </div>
      </div>
    </b2x.Offcanvas>
  );
};

const Header = () => {
  const { close } = b2x.useModalContext();

  return (
    <div className="mobile-navigation-offcanvas-header border-bottom position-relative py-2">
      <Container>
        <div className="text-center">
          <b2x.router.Link onClick={close} to="/">
            <b2x.Image src={srcMobile} />
          </b2x.router.Link>
        </div>
      </Container>
      <div className="position-absolute top-0 end-0 p-2">
        <Button className="text-white" iconEnd={{ name: 'close', size: 20 }} onClick={close} variant="blank" />
      </div>
    </div>
  );
};

interface FooterProps {}

const Footer = (props: FooterProps) => {
  return (
    <div className="mobile-navigation-offcanvas-footer">
      <div className="border-top">
        <DrawerLocaleItem />
      </div>

      <Container>
        <b2x.Div display={'flex'} justifyContent={'start'} paddingY={3}>
          <SocialIcons size={36} />
        </b2x.Div>
      </Container>
    </div>
  );
};

interface DrawerBackItemProps {
  label?: string;
}

const DrawerBackItem = ({ label = t('misc.back') }: DrawerBackItemProps) => {
  const { closeActiveDrawer } = b2x.useDrawersContext();

  return (
    <div>
      <b2x.DrawerButtonItem onClick={closeActiveDrawer}>
        <b2x.DrawerItem
          center={{ center: label }}
          className="small position-relative text-white letter-spacing-sm fw-bold"
          height={35}
          justifyContent="start"
          start={{ center: <Icon className="me-2" name="light-arrow-left" /> }}
        />
      </b2x.DrawerButtonItem>
    </div>
  );
};

interface DrawerMenuItemsProps {
  depth?: number;
  item: b2x.MenuApiDto;
  parentItem?: b2x.MenuApiDto;
}

const DrawerMenuItems = ({ depth = 1, item, parentItem }: DrawerMenuItemsProps) => {
  const _parentItem = depth === 2 ? item : parentItem;

  const { getPagePath } = b2x.useAppStaticContext();

  return (
    <>
      <div className="drawer-menu-items fw-light letter-spacing-sm">
        {item.children.map((child) => {
          const hasChildren = child.children.length > 0;
          const elementName = (
            <span className="fw-normal">
              {child.label}
              {child.subTitle ? (
                <span className="text-uppercase fw-light d-block mb-1 extra-small mb-1">{child.subTitle}</span>
              ) : (
                ''
              )}
            </span>
          );

          return hasChildren ? (
            <b2x.DrawerTriggerItem
              drawerButtonClassName={child.tags?.includes('featured') ? 'featured' : undefined}
              key={child.id}
              newDrawer={
                <Drawer backItemProps={{ label: child.label }}>
                  <DrawerMenuItems depth={depth + 1} item={child} parentItem={_parentItem} />
                </Drawer>
              }
            >
              <b2x.DrawerItem
                className={classnames('text-white fw-light letter-spacing-sm h6 m-0', child.cssClass)}
                end={{ center: <Icon name="light-arrow-right" size={10} /> }}
                height={46}
                justifyContent="between"
                start={{ center: elementName }}
              />
            </b2x.DrawerTriggerItem>
          ) : (
            <b2x.DrawerLinkItem
              className={classnames(child.cssClass, { featured: child.tags?.includes('featured') })}
              key={child.id}
              to={child.link}
            >
              <b2x.DrawerItem
                className={classnames(child.cssClass)}
                height={46}
                justifyContent="start"
                start={{ center: elementName }}
              />
            </b2x.DrawerLinkItem>
          );
        })}
        {/*         {_parentItem?.label && (
          <b2x.DrawerLinkItem to={_parentItem.link}>
            <b2x.DrawerItem
              className="fw-bold text-decoration-underline"
              height={46}
              justifyContent="start"
              start={{ center: _parentItem.note ?? t('misc.viewAll') }}
            />
          </b2x.DrawerLinkItem>
        )}
 */}
      </div>
      {_parentItem?.content &&
        b2x.typedContent<MenuMobileOffcanvasItemContentType>(_parentItem.content, (content) => (
          <div className="drawer-menu-items-content py-3">
            {content.body.ctaList && content.body.ctaList.length > 0 && (
              <Container>
                <b2x.Row gap={3}>
                  {content.body.ctaList.map(({ contentSectionId, cta, image }) => (
                    <b2x.Col key={contentSectionId} size={12}>
                      {image?.src?.xs ? (
                        <b2x.router.Link
                          className="border border-dark d-flex text-decoration-none text-uppercase small fw-bold"
                          closeModal
                          to={cta?.to?.href ? cta.to.href : cta?.to?.code && getPagePath(cta.to.code)}
                        >
                          <div className="w-50">
                            <b2x.ImageFromContentV1 className="w-100 h-100" style={{ objectFit: 'cover' }} {...image} />
                          </div>
                          <div
                            className="w-50 p-3 d-flex justify-content-start
                           "
                          >
                            {cta?.label}
                          </div>
                        </b2x.router.Link>
                      ) : (
                        <div className="d-grid">
                          <b2x.CtaFromContent
                            {...cta}
                            ctaProps={{
                              button: {
                                className: 'px-1',
                                iconStart: cta && isIconName(cta.icon) ? { name: cta.icon, size: 20 } : undefined,
                                justifyContent: 'start',
                              },
                              link: { closeModal: true },
                            }}
                            variant="outline-primary"
                          />
                        </div>
                      )}
                    </b2x.Col>
                  ))}
                </b2x.Row>
              </Container>
            )}
          </div>
        ))}
    </>
  );
};

interface DrawerProps {
  backItemProps?: DrawerBackItemProps;
  children: React.ReactNode;
}

const Drawer = ({ backItemProps, children }: DrawerProps) => {
  return (
    <div className="d-flex flex-column h-100 overflow-auto sub-drawer">
      <DrawerBackItem {...backItemProps} />
      <div className="flex-grow-1 overflow-auto">{children}</div>
    </div>
  );
};

const DrawerLocaleItem = () => {
  const { info, locale, shippingCountry } = b2x.useAppContext();

  return (
    <b2x.DrawerTriggerItem
      newDrawer={
        <Drawer>
          <div className="px-1 pt-4">
            <b2x.LocaleForm singleColumn />
            {info?.shippingCountries?.length && info.shippingCountries.length > 1 && (
              <div className="mt-3 text-center">
                <p>{t('misc.changeCountryMessage')}</p>
              </div>
            )}
          </div>
        </Drawer>
      }
    >
      <b2x.DrawerItem
        className="px-1 text-white text-uppercase"
        end={{ center: <Icon className="text-white" name="light-arrow-right" size={10} /> }}
        height={46}
        justifyContent="between"
        start={{
          center: (
            <span className="d-flex gap-2 h6 m-0 fw-light align-items-center">
              <Icon className="text-white" name={'language'} size={16} /> {shippingCountry?.code} | {locale?.code}
            </span>
          ),
        }}
      />
    </b2x.DrawerTriggerItem>
  );
};

export const useMobileNavigationOffcanvas = (props?: MobileNavigationOffcanvasProps) =>
  b2x.useStaticModal(MobileNavigationOffcanvas, 'offcanvas', props);
